import { ReactNode, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './SelectMedia.module.css';

export interface SelectMediaUploadProps {
  accept?: string;
  onSelect: (file: File) => void;
  disabled?: boolean;
  button: string;
  description?: ReactNode;
}

// lower the image quality to reduce the size
const IMAGE_QUALITY = 0.8;

const errToString = (err: any) => {
  if (typeof err?.response?.data?.msg === 'string') {
    return err?.response?.data?.msg;
  }
  if (typeof err?.error?.message === 'string') {
    return err?.error?.message;
  }
  if (typeof err?.message === 'string') {
    return err?.message;
  }
  return (
    JSON.stringify(err?.response?.data?.msg) ||
    err.toString() ||
    JSON.stringify(err) ||
    err ||
    'Something went wrong'
  );
};

export const MAX_FILE_SIZE = 10 ** 6 * 4.5;

export function SelectMediaUpload(props: SelectMediaUploadProps) {
  const { accept, onSelect, button, disabled, description } = props;

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className={styles.tabContent}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className={styles.uploadInput}
        onChange={(e) => {
          if (!e.target.files?.[0]) return;
          setLoading(true);
        }}
        onInput={(e) => {
          setErr(null);
          const file = (e.target as HTMLInputElement).files?.[0];
          if (!file) return;
          if (file.size > MAX_FILE_SIZE) {
            setErr(`Your file size cannot be bigger than ${MAX_FILE_SIZE / 10 ** 6}MB`);
            return;
          }
          setLoading(true);

          if (accept !== 'image/*' || ['image/gif', 'image/svg+xml'].includes(file.type)) {
            onSelect(file);
            setLoading(false);
            return;
          }

          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            const img = new Image();
            img.src = reader.result as string;
            img.onload = () => {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              if (!ctx) {
                setErr('oops, image context not found');
                setLoading(false);
                return;
              }
              // 8k max dimension
              const MAX_DIMENSION_PIXEL = 1024 * 8;
              if (img.width > MAX_DIMENSION_PIXEL) {
                canvas.width = MAX_DIMENSION_PIXEL;
                // set height maintaining aspect ratio
                canvas.height = img.height * (canvas.width / img.width);
              } else if (img.height > MAX_DIMENSION_PIXEL) {
                canvas.height = MAX_DIMENSION_PIXEL;
                // set width maintaining aspect ratio
                canvas.width = img.width * (canvas.height / img.height);
              } else {
                canvas.height = img.height;
                canvas.width = img.width;
              }

              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

              canvas.toBlob(
                async (blob) => {
                  if (!blob) {
                    console.error('oops, no blob');
                    setLoading(false);
                    return;
                  }

                  const f = new File([blob], file.name, { type: file.type });

                  try {
                    onSelect(f);
                  } catch (error) {
                    setLoading(false);
                    setErr(errToString(error));
                  }
                },
                file.type,
                IMAGE_QUALITY
              );
            };
          };
        }}
      />
      <button
        type="button"
        className={classNames(styles.uploadButton, { [styles.isLoading]: loading })}
        onClick={() => {
          inputRef.current?.click();
        }}
        disabled={disabled || loading}
      >
        {button}
      </button>

      {description && <div className={styles.description}>{description}</div>}
      {err && <div className={styles.errorMessage}>{err}</div>}
    </div>
  );
}

SelectMediaUpload.defaultProps = {
  accept: undefined,
  disabled: false,
  description: undefined,
};
